import React, { useMemo } from 'react';
import { Alert, AlertIcon, AlertTitle, Flex, Text } from '@chakra-ui/react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { type ApiError } from 'ts/common/common';
import ResponsiveButtonGroup from 'ts/common/components/ResponsiveButtonGroup';
import { ButtonProps } from 'ts/common/components/gallery';
import QuestionnaireQuestionField from 'ts/common/components/questionnaire/QuestionnaireQuestionField';
import { useTranslate } from 'ts/common/hooks';
import { multipleChoiceSeparator } from 'ts/common/questionnaire/common';

/**
 * Form values keyed by page index, then question id (string keys for react-hook-form path inference).
 * Text/dropdown/radio: string; checkboxes: string[] (choice text values).
 */
export type QuestionnaireFormValues = {
    pages: Record<string, Record<string, string | string[]>>;
};

function buildPageDefaultValues(
    questionnairePage: SpApi.Client.IQuestionnairePage,
    pageIndex: number
): QuestionnaireFormValues {
    const pageKey = String(pageIndex);
    const pageValues: Record<string, string | string[]> = {};

    questionnairePage.questions.forEach((question) => {
        const questionKey = String(question.id);

        if (question.responseType === 'choices_checkboxes') {
            pageValues[questionKey] = question.responseText?.split(multipleChoiceSeparator) ?? [];
        } else {
            pageValues[questionKey] = question.responseText ?? '';
        }
    });

    return { pages: { [pageKey]: pageValues } };
}

interface Props {
    error: Nullable<ApiError>;
    onGoBack?: () => unknown;
    onSave: (questionId: number, responseText: string | null) => Promise<unknown>;
    onSubmit: () => unknown | Promise<unknown>;
    pageIndex: number;
    questionnairePage: SpApi.Client.IQuestionnairePage;
    requestFailed: boolean;
    saving: boolean;
    submitting?: boolean;
    totalPages: number;
    isPreview?: boolean;
}

const QuestionnairePageForm: React.FC<Props> = ({
    error,
    onGoBack,
    onSave,
    onSubmit,
    pageIndex,
    questionnairePage,
    requestFailed,
    saving,
    submitting,
    isPreview = false,
    totalPages
}) => {
    const t = useTranslate('clientQuestionnaire');

    const defaultValues = useMemo(
        () => buildPageDefaultValues(questionnairePage, pageIndex),
        [questionnairePage, pageIndex]
    );

    const formMethods = useForm<QuestionnaireFormValues>({
        defaultValues
    });

    const { handleSubmit } = formMethods;

    const [, setSearchParams] = useSearchParams();

    const buttons: Array<ButtonProps> = [
        ...(pageIndex > 0 || !!onGoBack
            ? [
                  {
                      text: t('previousPage'),
                      variant: 'outline primary' as const,
                      onClick: () => {
                          if (pageIndex > 0) {
                              setSearchParams((oldParams) => {
                                  const newParams = new URLSearchParams(oldParams);
                                  newParams.set('page', String(pageIndex));
                                  return newParams;
                              });
                          } else {
                              onGoBack?.();
                          }
                      }
                  }
              ]
            : []),
        ...(pageIndex < totalPages - 1
            ? [
                  {
                      text: t('nextPage'),
                      variant: 'primary' as const,
                      onClick: () => {
                          handleSubmit(() => {
                              setSearchParams((oldParams) => {
                                  const newParams = new URLSearchParams(oldParams);
                                  newParams.set('page', String(pageIndex + 2));
                                  return newParams;
                              });
                          })();
                      }
                  }
              ]
            : []),
        ...(pageIndex === totalPages - 1
            ? [
                  {
                      text: submitting || saving ? t('saving') : t('submit'),
                      variant: 'primary' as const,
                      onClick: handleSubmit(onSubmit),
                      disabled: submitting || saving
                  }
              ]
            : [])
    ];

    return (
        <FormProvider {...formMethods}>
            <Flex flexDirection="column" marginY="16px" gap="16px">
                <Text as="h3" fontSize="18px" fontWeight="600">
                    {questionnairePage.name}
                </Text>
                {[...questionnairePage.questions]
                    .sort((a, b) => a.displayOrder - b.displayOrder)
                    .map((question) => (
                        <QuestionnaireQuestionField
                            key={question.id}
                            onSave={onSave}
                            pageIndex={pageIndex}
                            question={question}
                        />
                    ))}
            </Flex>
            {requestFailed && (
                <Alert status="error">
                    <AlertIcon />
                    <AlertTitle>{error?.detail ?? t('errorSavingQuestionnaire')}</AlertTitle>
                </Alert>
            )}
            {!isPreview && <ResponsiveButtonGroup buttons={buttons} />}
        </FormProvider>
    );
};

export default QuestionnairePageForm;
